import { css } from "@emotion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { trpcClient } from "../lib/trpc";
import { Button } from "./button";
import { Modal } from "./modal";
import { Plus, X } from "@phosphor-icons/react";

const searchContainer = css`
  position: relative;
  margin-bottom: 16px;
`;

const searchInput = css`
  width: 100%;
  padding: 14px 16px;
  border-radius: var(--radius-md);
  border: 2px solid var(--input-border);
  font-family: var(--font-body);
  font-size: 15px;
  color: var(--text-primary);
  background: var(--input-bg);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--input-border-focus);
    box-shadow: 0 0 0 3px var(--selected-word-bg);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const dropdown = css`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  margin-top: 4px;
  background: var(--container-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 16px var(--shadow-medium);
  max-height: 280px;
  overflow-y: auto;
`;

const dropdownItem = css`
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-bottom: 1px solid var(--color-border);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--dropdown-hover);
  }
`;

const dropdownItemName = css`
  font-family: var(--font-heading);
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
`;

const dropdownItemPhone = css`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin-top: 2px;
`;

const dropdownLoading = css`
  padding: 12px 16px;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  text-align: center;
`;

const dropdownNoResults = css`
  padding: 12px 16px;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  text-align: center;
`;

const dropdownCreateNew = css`
  padding: 12px 16px;
  cursor: pointer;
  transition: background 0.15s ease;
  border-top: 2px solid var(--color-border);
  background: var(--color-bg-warm);
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: var(--dropdown-hover);
  }
`;

const dropdownCreateIcon = css`
  font-size: 18px;
`;

const dropdownCreateText = css`
  font-family: var(--font-heading);
  font-size: 14px;
  font-weight: 600;
  color: var(--text-accent);
`;

const formGroup = css`
  margin-bottom: 16px;
`;

const formLabel = css`
  font-family: var(--font-heading);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-navy);
  display: block;
  margin-bottom: 6px;
`;

const formInput = css`
  width: 100%;
  padding: 12px 16px;
  border-radius: var(--radius-md);
  border: 2px solid var(--input-border);
  font-family: var(--font-body);
  font-size: 14px;
  color: var(--text-primary);
  background: var(--input-bg);
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--input-border-focus);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background: var(--color-bg-warm);
  }
`;

const playerInfoCard = css`
  background: var(--color-bg-warm);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  padding: 16px;
  margin-bottom: 20px;
`;

const playerInfoRow = css`
  display: flex;
  gap: 8px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const playerInfoLabel = css`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  min-width: 80px;
`;

const playerInfoValue = css`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
`;

const fieldError = css`
  color: var(--error-text);
  font-size: var(--text-sm);
  margin-top: 4px;
`;

const sectionTitle = css`
  font-family: var(--font-heading);
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-border);
`;

const gridResultsList = css`
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
`;

const gridResultItem = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  margin-bottom: 8px;
  background: var(--container-bg);

  &:last-child {
    margin-bottom: 0;
  }
`;

const gridResultInfo = css`
  flex: 1;
`;

const gridResultName = css`
  font-weight: 600;
  color: var(--text-primary);
`;

const gridResultDate = css`
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

const selectedGridsContainer = css`
  margin-top: 16px;
  padding: 12px;
  background: var(--success-bg);
  border-radius: var(--radius-md);
`;

const selectedGridsLabel = css`
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--success-text);
  margin-bottom: 8px;
`;

const selectedGridsChips = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const selectedGridChip = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: white;
  border: 1px solid var(--success-text);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12px;
  color: var(--success-text);
  font-weight: 500;
`;

const chipRemove = css`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 14px;
  padding: 0 2px;
  line-height: 1;

  &:hover {
    color: var(--color-danger, #e53e3e);
  }
`;

const modalActions = css`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid var(--color-border);
`;

const searchHint = css`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  margin-top: 8px;
`;

const formError = css`
  background: var(--error-bg);
  color: var(--error-text);
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  margin-bottom: 12px;
`;

type Player = {
  id: string;
  firstname: string;
  lastname: string;
  phone: string;
};

type GridSearchResult = {
  id: string;
  identifier: string;
  createdAt: Date | string;
};

type SelectedGrid = {
  id: string;
  identifier: string;
};

interface PlayerGridLinkModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  onSuccess: () => void;
  onPlayerSelected?: (player: { id: string; firstname: string; lastname: string; phone: string }) => void;
}

type Step = "search" | "link";

export function PlayerGridLinkModal({
  open,
  onClose,
  eventId,
  onSuccess,
  onPlayerSelected,
}: PlayerGridLinkModalProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gridSearchInputRef = useRef<HTMLInputElement>(null);

  // Step management
  const [step, setStep] = useState<Step>("search");

  // Player search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Player[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Selected/New player state
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [isNewPlayer, setIsNewPlayer] = useState(false);
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Grid search state
  const [gridQuery, setGridQuery] = useState("");
  const [gridResults, setGridResults] = useState<GridSearchResult[]>([]);
  const [isSearchingGrids, setIsSearchingGrids] = useState(false);
  const [selectedGrids, setSelectedGrids] = useState<SelectedGrid[]>([]);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [addingGridId, setAddingGridId] = useState<string | null>(null);

  const FRENCH_PHONE_REGEX = /^(?:0[1-9]\d{8}|\+33[1-9]\d{8})$/;
  const normalizePhone = (v: string) => v.replace(/[\s.\-]/g, "");
  const isPhoneValid = (v: string) => FRENCH_PHONE_REGEX.test(normalizePhone(v));

  const formatPhone = (phoneNum: string): string => {
    if (phoneNum.startsWith("+33")) {
      const withoutPrefix = phoneNum.slice(3);
      return "0" + withoutPrefix.replace(/(\d{1})(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
    }
    if (phoneNum.length === 10 && phoneNum.startsWith("0")) {
      return phoneNum.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, "$1 $2 $3 $4 $5");
    }
    return phoneNum;
  };

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setStep("search");
      setSearchQuery("");
      setSearchResults([]);
      setIsSearching(false);
      setShowDropdown(false);
      setHasSearched(false);
      setSelectedPlayer(null);
      setIsNewPlayer(false);
      setFirstname("");
      setLastname("");
      setPhone("");
      setPhoneError(null);
      setGridQuery("");
      setGridResults([]);
      setSelectedGrids([]);
      setSubmitError(null);
    }
  }, [open]);

  // Player search by phone effect
  const searchPlayersByPhone = useCallback(async (phone: string) => {
    if (phone.length < 2) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);

    try {
      const data = await trpcClient.searchPlayersByPhone.query({
        phone,
        limit: 5,
      });
      setSearchResults(data);
    } catch (error) {
      console.error("Error searching players:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    if (step !== "search") return;

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (searchQuery.length >= 2) {
      debounceRef.current = setTimeout(() => {
        searchPlayersByPhone(searchQuery);
      }, 300);
    } else {
      setSearchResults([]);
      setHasSearched(false);
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, searchPlayersByPhone, step]);

  // Grid search effect
  useEffect(() => {
    if (step !== "link") return;

    const trimmed = gridQuery.trim();
    if (trimmed.length < 3) {
      setGridResults([]);
      return;
    }

    setIsSearchingGrids(true);

    const handle = setTimeout(async () => {
      try {
        const results = await trpcClient.searchGridsByIdentifier.query({
          identifier: trimmed,
          eventId,
        });
        // Filter out already selected grids
        const filtered = results.filter(
          (g: GridSearchResult) => !selectedGrids.some((sg) => sg.id === g.id)
        );
        setGridResults(filtered);
      } catch (error) {
        console.error("Error searching grids:", error);
        setGridResults([]);
      } finally {
        setIsSearchingGrids(false);
      }
    }, 400);

    return () => clearTimeout(handle);
  }, [gridQuery, eventId, selectedGrids, step]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectPlayer = (player: Player) => {
    if (onPlayerSelected) {
      // Delegate to parent to open edit modal
      onPlayerSelected(player);
      onClose();
      return;
    }
    setSelectedPlayer(player);
    setIsNewPlayer(false);
    setStep("link");
    setShowDropdown(false);
  };

  const handleCreateNew = () => {
    // Pre-fill phone with search query (since search is phone-only)
    const trimmedPhone = searchQuery.trim();
    if (trimmedPhone) {
      setPhone(trimmedPhone);
    }

    setSelectedPlayer(null);
    setIsNewPlayer(true);
    setStep("link");
    setShowDropdown(false);
  };

  const handleAddGrid = async (grid: GridSearchResult) => {
    // Validate new player data
    if (isNewPlayer) {
      if (!firstname.trim() || !lastname.trim() || !phone.trim()) {
        setSubmitError(t("playerGridLink.fillAllFields"));
        return;
      }
      if (!isPhoneValid(phone)) {
        setPhoneError(t("eventDetail.phoneError"));
        return;
      }
    }

    setAddingGridId(grid.id);
    setSubmitError(null);

    try {
      await trpcClient.playGridAutomatically.mutate({
        gridId: grid.id,
        eventId,
        firstname: selectedPlayer?.firstname ?? firstname.trim(),
        lastname: selectedPlayer?.lastname ?? lastname.trim(),
        phone: selectedPlayer?.phone ?? normalizePhone(phone),
      });

      setSelectedGrids((prev) => [
        ...prev,
        { id: grid.id, identifier: grid.identifier ?? "—" },
      ]);
      setGridResults((prev) => prev.filter((g) => g.id !== grid.id));
      setGridQuery("");

      // Focus back on grid search input
      setTimeout(() => {
        gridSearchInputRef.current?.focus();
      }, 0);
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : t("playerGridLink.addError")
      );
    } finally {
      setAddingGridId(null);
    }
  };

  const handleRemoveGrid = async (gridId: string) => {
    try {
      await trpcClient.removeGridFromAutoPlay.mutate({ gridId, eventId });
      setSelectedGrids((prev) => prev.filter((g) => g.id !== gridId));
    } catch (error) {
      console.error("Error removing grid:", error);
    }
  };

  const handleClose = () => {
    if (selectedGrids.length > 0) {
      onSuccess();
    }
    onClose();
  };

  const handleBack = () => {
    setStep("search");
    setSelectedPlayer(null);
    setIsNewPlayer(false);
    setFirstname("");
    setLastname("");
    setPhone("");
    setPhoneError(null);
    setGridQuery("");
    setGridResults([]);
    setSelectedGrids([]);
  };

  const renderSearchStep = () => (
    <>
      <p css={css`color: var(--text-secondary); margin-bottom: 16px; font-size: var(--text-sm);`}>
        {t("playerGridLink.searchDescription")}
      </p>

      <div css={searchContainer} ref={containerRef}>
        <input
          type="text"
          css={searchInput}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder={t("playerGridLink.searchPlaceholder")}
          autoFocus
        />

        {showDropdown && searchQuery.length >= 2 && (
          <div css={dropdown}>
            {isSearching && (
              <div css={dropdownLoading}>{t("eventDetail.searchingPlayers")}</div>
            )}

            {!isSearching &&
              searchResults.map((player) => (
                <div
                  key={player.id}
                  css={dropdownItem}
                  onClick={() => handleSelectPlayer(player)}
                >
                  <div css={dropdownItemName}>
                    {player.firstname} {player.lastname}
                  </div>
                  <div css={dropdownItemPhone}>{formatPhone(player.phone)}</div>
                </div>
              ))}

            {!isSearching && hasSearched && (
              <div css={dropdownCreateNew} onClick={handleCreateNew}>
                <span css={dropdownCreateIcon}><Plus size={18} weight="bold" /></span>
                <span css={dropdownCreateText}>
                  {searchResults.length === 0
                    ? t("playerGridLink.createNewPlayer")
                    : t("playerGridLink.createNewPlayerAnyway")}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {searchQuery.length > 0 && searchQuery.length < 2 && (
        <p css={searchHint}>{t("playerGridLink.minChars")}</p>
      )}

      <div css={modalActions}>
        <Button onClick={onClose} variant="secondary">
          {t("common.cancel")}
        </Button>
      </div>
    </>
  );

  const renderLinkStep = () => (
    <>
      {/* Player info section */}
      {selectedPlayer && (
        <div css={playerInfoCard}>
          <div css={playerInfoRow}>
            <span css={playerInfoLabel}>{t("eventDetail.firstname")}</span>
            <span css={playerInfoValue}>{selectedPlayer.firstname}</span>
          </div>
          <div css={playerInfoRow}>
            <span css={playerInfoLabel}>{t("eventDetail.lastname")}</span>
            <span css={playerInfoValue}>{selectedPlayer.lastname}</span>
          </div>
          <div css={playerInfoRow}>
            <span css={playerInfoLabel}>{t("eventDetail.phone")}</span>
            <span css={playerInfoValue}>{formatPhone(selectedPlayer.phone)}</span>
          </div>
        </div>
      )}

      {/* New player form */}
      {isNewPlayer && (
        <>
          <div css={formGroup}>
            <label css={formLabel}>{t("eventDetail.firstname")} *</label>
            <input
              type="text"
              css={formInput}
              value={firstname}
              onChange={(e) => setFirstname(e.target.value)}
              placeholder={t("eventDetail.firstnamePlaceholder")}
              disabled={selectedGrids.length > 0}
            />
          </div>
          <div css={formGroup}>
            <label css={formLabel}>{t("eventDetail.lastname")} *</label>
            <input
              type="text"
              css={formInput}
              value={lastname}
              onChange={(e) => setLastname(e.target.value)}
              placeholder={t("eventDetail.lastnamePlaceholder")}
              disabled={selectedGrids.length > 0}
            />
          </div>
          <div css={formGroup}>
            <label css={formLabel}>{t("eventDetail.phone")} *</label>
            <input
              type="tel"
              css={formInput}
              value={phone}
              onChange={(e) => {
                setPhone(e.target.value);
                if (phoneError) setPhoneError(null);
              }}
              placeholder={t("eventDetail.phonePlaceholder")}
              disabled={selectedGrids.length > 0}
            />
            {phoneError && <div css={fieldError}>{phoneError}</div>}
          </div>
        </>
      )}

      {/* Selected grids */}
      {selectedGrids.length > 0 && (
        <div css={selectedGridsContainer}>
          <div css={selectedGridsLabel}>
            {t("playerGridLink.linkedGrids", { count: selectedGrids.length })}
          </div>
          <div css={selectedGridsChips}>
            {selectedGrids.map((grid) => (
              <span key={grid.id} css={selectedGridChip}>
                {grid.identifier}
                <button
                  type="button"
                  css={chipRemove}
                  onClick={() => handleRemoveGrid(grid.id)}
                  title={t("eventDetail.removeGrid")}
                >
                  <X size={12} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Grid search section */}
      <div css={css`margin-top: 20px;`}>
        <div css={sectionTitle}>{t("playerGridLink.searchGrids")}</div>

        <div css={formGroup}>
          <input
            ref={gridSearchInputRef}
            type="text"
            css={formInput}
            value={gridQuery}
            onChange={(e) => setGridQuery(e.target.value)}
            placeholder={t("eventDetail.gridIdentifierPlaceholder")}
          />
        </div>

        {gridQuery.trim().length > 0 && gridQuery.trim().length < 3 && (
          <p css={searchHint}>{t("eventDetail.minChars")}</p>
        )}

        {isSearchingGrids && (
          <p css={searchHint}>{t("eventDetail.searchingGrids")}</p>
        )}

        {submitError && <div css={formError}>{submitError}</div>}

        {!isSearchingGrids && gridQuery.trim().length >= 3 && gridResults.length === 0 && (
          <p css={searchHint}>{t("eventDetail.noGridsFound")}</p>
        )}

        {gridResults.length > 0 && (
          <ul css={gridResultsList}>
            {gridResults.map((grid) => (
              <li key={grid.id} css={gridResultItem}>
                <div css={gridResultInfo}>
                  <div css={gridResultName}>
                    {grid.identifier ?? t("eventDetail.noIdentifier")}
                  </div>
                  <div css={gridResultDate}>
                    {t("eventDetail.createdAt", {
                      date: grid.createdAt
                        ? new Date(grid.createdAt).toLocaleString()
                        : t("eventDetail.unknownDate"),
                    })}
                  </div>
                </div>
                <Button
                  onClick={() => handleAddGrid(grid)}
                  variant="primary"
                  size="xs"
                  disabled={addingGridId === grid.id}
                >
                  {addingGridId === grid.id ? t("eventDetail.adding") : t("common.add")}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div css={modalActions}>
        {selectedGrids.length === 0 && (
          <Button onClick={handleBack} variant="secondary">
            {t("common.back")}
          </Button>
        )}
        <Button onClick={handleClose} variant={selectedGrids.length > 0 ? "primary" : "secondary"}>
          {selectedGrids.length > 0 ? t("playerGridLink.done") : t("common.cancel")}
        </Button>
      </div>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={
        step === "search"
          ? t("playerGridLink.title")
          : selectedPlayer
            ? t("playerGridLink.linkGridsTo", {
                name: `${selectedPlayer.firstname} ${selectedPlayer.lastname}`,
              })
            : t("playerGridLink.newPlayerAndGrids")
      }
    >
      {step === "search" ? renderSearchStep() : renderLinkStep()}
    </Modal>
  );
}
